"use strict";Object.defineProperty(exports, "__esModule", {value: true}); function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }var _time = require('./time');
var _Message = require('shared/components/Message');
var _lodash = require('lodash');
var _uuid = require('shared/services/uuid');
var _message = require('shared/enum/message');
var _previewSupport = require('shared/utils/previewSupport');
var _scheduledMessages = require('shared/services/scheduledMessages');




/* The message fields processMessages reads. Every other field is copied onto
 * the row unchanged. */
















































































































/*
 * Row-identity cache for processMessages. Each pass rebuilds every feed item,
 * so without this every row would get a new object and re-render. A cached
 * row is returned only when it is deep-equal to the freshly built one, so the
 * cache can never serve stale data; it only preserves object identity.
 *
 * It is module-level on purpose: the chat selectors are shared across the
 * app, and a module cache survives selector re-creation. It is therefore
 * shared by every store instance (tests, logout/login on web). That is safe
 * for the same reason: reuse requires deep equality with the new item.
 *
 * Bounded to MAX_CONVERSATION_CACHES conversations (least recently used is
 * evicted). Within a conversation, entries are trimmed only after a pass and
 * never below the size of that pass, so a single pass always reuses every
 * unchanged row however long the conversation is. processMessageBatches runs
 * one selector pass as several (one per loaded batch) and trims once, after
 * the last batch, never below the whole run, so batches that together exceed
 * the cap still reuse every unchanged row.
 */
const MAX_CONVERSATION_CACHES = 10
const MAX_ITEMS_PER_CONVERSATION = 2000
const processedItemCaches = new Map()

const getProcessedItemCacheKey = (
  conversation,
  blobject,
) => {
  const conversationKey = _optionalChain([conversation, 'optionalAccess', _ => _.ConversationKey])
  if (!conversationKey) return null
  return `${conversationKey}:${_optionalChain([blobject, 'optionalAccess', _2 => _2.Key]) || 'no-blobject'}`
}

const getProcessedItemCache = (
  conversation,
  blobject,
) => {
  const cacheKey = getProcessedItemCacheKey(conversation, blobject)
  if (!cacheKey) return null

  const existing = processedItemCaches.get(cacheKey)
  if (existing) {
    processedItemCaches.delete(cacheKey)
    processedItemCaches.set(cacheKey, existing)
    return existing
  }

  const cache = new Map()
  processedItemCaches.set(cacheKey, cache)
  if (processedItemCaches.size > MAX_CONVERSATION_CACHES) {
    // The map is over its limit, so it has a first key.
    processedItemCaches.delete(processedItemCaches.keys().next().value )
  }
  return cache
}

// The conversation's cache without creating it or moving it in the LRU order.
const peekProcessedItemCache = (
  conversation,
  blobject,
) => {
  const cacheKey = getProcessedItemCacheKey(conversation, blobject)
  return cacheKey ? processedItemCaches.get(cacheKey) : undefined
}

/* Entries are kept in least-recently-used order, so the ones this pass did
 * not touch are at the front. Evicting one at a time during a pass would
 * thrash on any pass larger than the cap (each eviction removes the next
 * entry the pass needs), so trim once at the end instead. */
const trimProcessedItemCache = (cache, passEntries) => {
  if (!cache) return
  const limit = Math.max(MAX_ITEMS_PER_CONVERSATION, passEntries)
  const keys = cache.keys()
  while (cache.size > limit) {
    // The cache is over its limit, so the iterator has a next key.
    cache.delete(keys.next().value )
  }
}

/* A subscriber's SeenBy entry only affects how a row renders when it is that
 * subscriber's latest-seen row (the row shows their avatar). On other rows the
 * entry just records that they have seen it, so keep the previous entry while
 * that stays true; otherwise every seen-time move (e.g. the mark-seen after
 * each send) would give every earlier row a new SeenBy and re-render it. */
const reconcileSeenBy = (previousSeenBy, seenBy) => {
  if (!previousSeenBy) return seenBy
  const keys = Object.keys(seenBy)
  if (keys.length !== Object.keys(previousSeenBy).length) return seenBy

  let changed = false
  const reconciled = {}
  keys.forEach(key => {
    const previous = previousSeenBy[key]
    // key comes from Object.keys(seenBy).
    const next = seenBy[key]
    const keepPrevious = previous && (
      _lodash.isEqual.call(void 0, previous, next) ||
      (!previous.IsLatestMessage && !next.IsLatestMessage)
    )
    reconciled[key] = keepPrevious ? previous : next
    if (!keepPrevious) changed = true
  })

  return changed ? reconciled : previousSeenBy
}

const preserveProcessedItemIdentity = (
  cache,
  key,
  item,
  dependencies,
) => {
  if (!cache) return item

  const previous = cache.get(key)
  const reconciledItem = _optionalChain([previous, 'optionalAccess', _3 => _3.item, 'optionalAccess', _4 => _4.SeenBy]) && item.SeenBy
    ? { ...item, SeenBy: reconcileSeenBy(previous.item.SeenBy, item.SeenBy) }
    : item
  // A cached item deep-equal to this one was built from the same inputs by
  // the same code, so it has type T too.
  const stableItem = previous &&
    _lodash.isEqual.call(void 0, previous.dependencies, dependencies) &&
    _lodash.isEqual.call(void 0, previous.item, reconciledItem)
    ? previous.item 
    : reconciledItem

  cache.delete(key)
  cache.set(key, { dependencies, item: stableItem })

  return stableItem
}

function sameDate (a, b) {
  const d1 = new Date(a)
  const d2 = new Date(b)

  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate()
}

const THIRTY_MINUTES = 60 * 30

const isSimilarTime = (a, b, delta = THIRTY_MINUTES) => {
  // getTime() is the valueOf() that `-` applied to each Date.
  const actualDeltaInSeconds = Math.abs(new Date(a).getTime() - new Date(b).getTime()) / 1000
  return actualDeltaInSeconds < delta
}

const getEffectiveSender = (message) => {
  const senderKey = [message.PatientKey, message.UserKey, message.TeamKey, message.APIKeyKey]
    .find(key => key && key !== _uuid.EMPTY_BLUID)

  return senderKey
    ? { key: senderKey, type: message.SenderType }
    : null
}

const isSameSender = (message, otherMessage) => {
  const sender = getEffectiveSender(message)
  const otherSender = getEffectiveSender(otherMessage)

  return sender !== null && otherSender !== null &&
    sender.key === otherSender.key && sender.type === otherSender.type
}

/* Which send a row belongs to. The server writes every part of one send with
 * one SentAt, and the optimistic rows share the client's; while the echoes
 * replace them one by one, an echoed part keeps the client's as LocalSentAt. */
const sendOf = (message) => _nullishCoalesce(message.LocalSentAt, () => ( message.SentAt))

const isSameSend = (message, otherMessage) =>
  sendOf(message) === sendOf(otherMessage) && isSameSender(message, otherMessage)

const hasNotFailed = (message) =>
  message.deliveryStatus !== _message.deliveryStatus.FAILED && message.deliveryStatus !== _message.deliveryStatus.CANCELED

/* Text sent with files as one message is stored right after them in the same
 * send (sendConversationAttachments); it captions the last file. A failed
 * part keeps its own row and its Retry. */
const isCaptionOf = (text, file) =>
  !!text && !!file &&
  text.MessageType === _Message.MessageTypes.TEXT && file.MessageType === _Message.MessageTypes.FILE &&
  isSameSend(text, file) &&
  !text.Deleted && !file.Deleted && !text.ReplyToMessageKey &&
  hasNotFailed(text) && hasNotFailed(file)







/* Whether a feed row draws a message: its own, the caption it carries, or,
 * for a grid row, any image of its group. */
 const rowShowsMessage = (row, messageKey) =>
  !!messageKey && (
    row.MessageKey === messageKey ||
    _optionalChain([row, 'access', _5 => _5.Caption, 'optionalAccess', _6 => _6.MessageKey]) === messageKey ||
    !!_optionalChain([row, 'access', _7 => _7.ImageGroup, 'optionalAccess', _8 => _8.some, 'call', _9 => _9(groupRow => groupRow.MessageKey === messageKey)])
  ); exports.rowShowsMessage = rowShowsMessage

/* Where the "N new messages" line goes in a feed (newest first) and what it
 * counts, or null when there is no line. Only messages from others count:
 * text and files, not your own sends, not notices like "X was added" and not
 * scheduled messages (they aren't feed rows). The line sits under the oldest
 * message that counts, so your own replies and notices after the last one you
 * saw stay above it, and a feed with nothing new from others gets no line
 * rather than "0 new messages". `index` is the row the line is drawn under.
 * `knownCount` is the unread count the server gave when the line was placed:
 * the line says it when more is unread than the rows on screen show. */
 const newMessagesLine = (
  rows,
  lastSeenMessageKey,
  currentUserKey,
  knownCount = 0,
) => {
  const seenIndex = rows.findIndex(row => exports.rowShowsMessage.call(void 0, row, lastSeenMessageKey))
  if (seenIndex <= 0) return null
  let count = 0
  let oldestNew = -1
  for (let i = 0; i < seenIndex; i++) {
    const row = rows[i]
    if ((row.MessageType === _Message.MessageTypes.TEXT || row.MessageType === _Message.MessageTypes.FILE) &&
      row.UserKey !== currentUserKey) {
      count++
      oldestNew = i
    }
  }
  return count > 0 ? { index: oldestNew + 1, count: Math.max(count, knownCount) } : null
}; exports.newMessagesLine = newMessagesLine

/* An unrecalled photo or video, sent, still uploading or failed: each tile
 * shows its own upload state and Retry, and one with no thumbnail yet shows
 * a placeholder (a video's with its play icon). Other files keep their own
 * bubble, and a canceled upload is about to go. Replies keep their own
 * bubble (a send that replies quotes on its first file), so they never join. */
const isGroupableMedia = (message) => {
  const info = message.FileInfo
  const kind = _previewSupport.getMimeFirstMediaKind.call(void 0, _nullishCoalesce(_nullishCoalesce(_optionalChain([info, 'optionalAccess', _10 => _10.Mime]), () => ( _optionalChain([info, 'optionalAccess', _11 => _11.LocalMime]))), () => ( '')), _nullishCoalesce(message.FileName, () => ( '')))
  return message.MessageType === _Message.MessageTypes.FILE &&
    !message.Deleted &&
    !message.ReplyToMessageKey &&
    message.deliveryStatus !== _message.deliveryStatus.CANCELED &&
    (kind === 'image' || kind === 'video')
}

/* Runs of two or more consecutive photos and videos of one send (sendOf), as
 * [first, last] indexes into messages (oldest first). Two sends never merge,
 * however close in time. */
const findImageGroups = (messages) => {
  const groups = []
  let start = -1
  messages.forEach((message, i) => {
    const previous = messages[i - 1]
    const continuesRun = start !== -1 && previous && isGroupableMedia(message) &&
      isSameSend(previous, message)
    if (continuesRun) return
    if (start !== -1 && i - 1 > start) groups.push([start, i - 1])
    start = isGroupableMedia(message) ? i : -1
  })
  if (start !== -1 && messages.length - 1 > start) groups.push([start, messages.length - 1])
  return groups
}

/* Pairs each caption with its file, as feed indexes. A caption belongs to the
 * last file of its send; when that file is deleted it moves to the file before
 * it in the same send, and with none left it stands as ordinary text. */
const findCaptions = (messages) => {
  const fileCaption = new Map()
  const captions = new Set()
  messages.forEach((text, i) => {
    if (text.MessageType !== _Message.MessageTypes.TEXT) return
    for (let j = i - 1; j >= 0; j--) {
      const file = messages[j]
      if (isCaptionOf(text, file)) {
        fileCaption.set(j, i)
        captions.add(i)
        return
      }
      const deletedFromSameSend = file.Deleted && file.MessageType === _Message.MessageTypes.FILE &&
        isSameSend(text, file)
      if (!deletedFromSameSend) return
    }
  })
  return { fileCaption, captions }
}

/* Parameters:
 * messages: a sorted array of backend message objects (for struct def visit: conversation_view.go)
 * conversation: a backend conversation object (for struct def visit: conversation_view.go)
 * blobject: a backend blobject object (for struct def visit: blobject_view.go)
 */
const EMPTY_ARR = []












 function processMessages (
  messages = EMPTY_ARR,
  conversation,
  blobject,
) {
  const { feedItems, cacheEntries } = processMessagePass(messages, conversation, blobject)
  if (cacheEntries) {
    trimProcessedItemCache(peekProcessedItemCache(conversation, blobject), cacheEntries)
  }
  return feedItems
} exports.processMessages = processMessages;

/*
 * processMessages for each of a conversation's loaded batches (the chat
 * selector splits the store's messages at sequence gaps), as one run.
 *
 * Each batch is its own pass, so trimming after each (as a lone
 * processMessages call does, to max(cap, that batch)) evicts the other
 * batches' rows whenever the batches together exceed the cap, or whenever an
 * earlier batch grows (an older page loaded, a middle batch extended) while
 * the cache is over the cap, re-creating those rows on the next pass. Here
 * the cache is trimmed once, after the last batch, to max(cap, the run), so
 * every unchanged row is reused and the cache still holds at most the larger
 * of the cap and one run.
 *
 * When the old per-batch trims would evict nothing (the cache stays within
 * the cap through the run, as it does for any conversation whose loaded rows
 * fit the cap), the single trim evicts nothing either, so the cache and the
 * rows are exactly those of calling processMessages per batch. Otherwise it
 * keeps rows a per-batch trim would have dropped; a kept row is still reused
 * only when deep-equal to the rebuilt one, so no stale row is ever served.
 */
 function processMessageBatches (
  batches,
  conversation,
  blobject,
) {
  let runEntries = 0
  const feeds = batches.map(batch => {
    const { feedItems, cacheEntries } = processMessagePass(batch, conversation, blobject)
    runEntries += cacheEntries
    return feedItems
  })

  // Peeked, not fetched: an empty run never created or touched the cache.
  if (runEntries) trimProcessedItemCache(peekProcessedItemCache(conversation, blobject), runEntries)
  return feeds
} exports.processMessageBatches = processMessageBatches;







/* One pass over a batch, reusing cached rows; the caller trims the cache to
 * at least the entries the pass used. That is more than the rows it returns:
 * each image grid also keeps its newest image's own tile. */
function processMessagePass (
  messages,
  conversation,
  blobject,
) {
  /* TODO: is proper conversation */
  if (!messages.length || !conversation) return { feedItems: EMPTY_ARR, cacheEntries: 0 }

  /* helper object for latest time seen for each user */
  const seenTimes = {}

  if (blobject) {
    const pSubscribers = blobject.PatientSubscribers
      ? Object.values(blobject.PatientSubscribers)
      : EMPTY_ARR
    const uSubscribers = blobject.UserMetadata
      ? Object.values(blobject.UserMetadata)
      : EMPTY_ARR

    pSubscribers.concat(uSubscribers).forEach((subscriber) => {
      // Every subscriber has one of the two keys.
      const subscriberKey = (subscriber.UserKey || subscriber.PatientKey) 
      seenTimes[subscriberKey] = subscriber.SeenTime
    })
  }

  const feedItems = []
  const processedItemCache = getProcessedItemCache(conversation, blobject)
  const imageGroupByIndex = new Map()
  const imageGroups = findImageGroups(messages)
  const imageGroupCount = imageGroups.length
  imageGroups.forEach(group => {
    for (let i = group[0]; i <= group[1]; i++) imageGroupByIndex.set(i, group)
  })
  let groupRows = []
  const { fileCaption, captions } = findCaptions(messages)
  messages.forEach((message, i) => {
    const previousMessage = messages[i - 1]
    // A caption is drawn in its file's bubble.
    if (captions.has(i)) return
    const captionIndex = fileCaption.get(i)
    const Caption = captionIndex === undefined ? undefined : messages[captionIndex]
    // The next row drawn: captions have none of their own.
    let nextIndex = i + 1
    while (captions.has(nextIndex)) nextIndex++
    const nextMessage = messages[nextIndex]
    const isLastMessage = !nextMessage
    const isFirstMessage = !previousMessage

    const SeenBy = {}
    Object.keys(seenTimes).forEach(uk => {
      // uk comes from Object.keys(seenTimes).
      const latestSeenTime = seenTimes[uk]
      if (latestSeenTime >= message.SentAt) {
        const isLatestSeen = isLastMessage || nextMessage.SentAt > latestSeenTime
        const seenAtObject = {
          SeenTime: latestSeenTime, /* in the future, when user actually saw */
          SubscriberKey: uk,
          IsLatestMessage: isLatestSeen,
        }
        SeenBy[uk] = seenAtObject
      }
    })

    const SameSenderAsNextMessage = nextMessage &&
                                    isSameSender(message, nextMessage) &&
                                    isSimilarTime(nextMessage.SentAt, message.SentAt, THIRTY_MINUTES)

    const SameSenderAsPreviousMessage = previousMessage &&
                                        isSameSender(message, previousMessage) &&
                                        isSimilarTime(previousMessage.SentAt, message.SentAt, THIRTY_MINUTES)

    if (isFirstMessage || !sameDate(previousMessage.SentAt, message.SentAt)) {
      const dateMessage = generateDateMessage(message.SentAt)
      feedItems.unshift(preserveProcessedItemIdentity(
        processedItemCache,
        `date:${dateMessage.MessageKey}:${message.MessageKey}`,
        dateMessage,
        { sentAt: message.SentAt },
      ))
    }

    const processedMessage = {
      ...message,
      SeenBy,
      SameSenderAsNextMessage,
      SameSenderAsPreviousMessage,
      ...(Caption && { Caption }),
    }
    const imageGroup = imageGroupByIndex.get(i)
    if (imageGroup && i !== imageGroup[1]) processedMessage.InImageGroup = true
    /* Seen state is not a dependency: SeenBy is part of the item, which is
     * deep-compared after reconcileSeenBy, so a row is rebuilt only when its
     * rendered seen state (who has seen it, and whose latest row it is)
     * changes. */
    const dependencies = {
      previousMessage: previousMessage && {
        UserKey: previousMessage.UserKey,
        TeamKey: previousMessage.TeamKey,
        PatientKey: previousMessage.PatientKey,
        APIKeyKey: previousMessage.APIKeyKey,
        SentAt: previousMessage.SentAt,
      },
      nextMessage: nextMessage && {
        UserKey: nextMessage.UserKey,
        TeamKey: nextMessage.TeamKey,
        PatientKey: nextMessage.PatientKey,
        APIKeyKey: nextMessage.APIKeyKey,
        SentAt: nextMessage.SentAt,
      },
    }
    let row
    if (imageGroup && i === imageGroup[1]) {
      // The newest image's own row, kept stable like any other, is the
      // group's last tile; the grid row then draws the whole group.
      const ownRow = preserveProcessedItemIdentity(
        processedItemCache,
        `image:${message.MessageKey}`,
        processedMessage,
        dependencies,
      )
      const groupSeenBy = { ...ownRow.SeenBy }
      groupRows.forEach(groupRow => Object.entries(groupRow.SeenBy).forEach(([key, entry]) => {
        // A reader whose latest seen row is a tile shows on the grid.
        if (entry.IsLatestMessage) groupSeenBy[key] = entry
      }))
      row = preserveProcessedItemIdentity(
        processedItemCache,
        `message:${message.MessageKey}`,
        {
          ...ownRow,
          SeenBy: groupSeenBy,
          // The grid sits where its first image would, under the same name line.
          SameSenderAsPreviousMessage: groupRows[0].SameSenderAsPreviousMessage,
          ImageGroup: [...groupRows, ownRow],
        },
        dependencies,
      )
      groupRows = []
    } else {
      row = preserveProcessedItemIdentity(
        processedItemCache,
        `message:${message.MessageKey}`,
        processedMessage,
        dependencies,
      )
      if (row.InImageGroup) groupRows.push(row)
    }
    feedItems.unshift(row)
  })

  // One entry per row, plus each grid's tile entry.
  return { feedItems, cacheEntries: feedItems.length + imageGroupCount }
}

/* get chat-ready date string from  */
function generateDateMessage (dateISOString) {
  const date = new Date(dateISOString)
  date.setHours(0, 0, 0, 0)
  const isToday = sameDate(date, new Date())
  const midnightString = date.toISOString()
  return {
    MessageType: _Message.MessageTypes.DATE,
    MessageKey: midnightString,
    MessageText: isToday ? "Today" : _time.getDateString.call(void 0, date),
    SentAt: midnightString,
    Sequence: -1,
    SenderType: _Message.SenderTypes.NONE,
  }
}

/* What a staff member can do with a scheduled row from its bubble. Retry
 * and reschedule are for a failed row. */


/* Failures that sending again can't fix: a failed row with one of these
 * offers only Dismiss (and its text to copy). The server refuses a retry
 * for these too (scheduled_message_retry_refused). */
 const UNRETRYABLE_FAILURE_REASONS = new Set([
  'opted_out', 'carrier_blocked', 'admin_blocked', 'unreachable', 'patient_removed',
]); exports.UNRETRYABLE_FAILURE_REASONS = UNRETRYABLE_FAILURE_REASONS

/* Who is looking at a chat's scheduled messages: their key, their teams,
 * and the orgs they're an approved admin of. */
























const PATIENT_CHAT_TYPES = new Set(['patient_conversation', 'sms_patient_conversation'])
const SCHEDULED_VIEWER_ROLES = new Set(['author', 'teammate', 'orgAdmin', 'viewer'])

 const scheduledRoleFor = (
  row,
  viewer,
  chatType,
) => {
  // The server's word wins; the rest is for an older server that sends none.
  if (row.ViewerRole && SCHEDULED_VIEWER_ROLES.has(row.ViewerRole)) return row.ViewerRole
  if (viewer.userKey && row.AuthorKey === viewer.userKey) return 'author'
  if (!chatType || !PATIENT_CHAT_TYPES.has(chatType)) return null
  if (row.OrgKey && _optionalChain([viewer, 'access', _12 => _12.adminOrgs, 'optionalAccess', _13 => _13.has, 'call', _14 => _14(row.OrgKey)])) return 'orgAdmin'
  if (row.TeamKey && _optionalChain([viewer, 'access', _15 => _15.teams, 'optionalAccess', _16 => _16[row.TeamKey]])) return 'teammate'
  return null
}; exports.scheduledRoleFor = scheduledRoleFor

/* The dashed Scheduled bubble the feed adds after a chat's real messages.
 * Like the day separators it is a non-message row; unlike them it never sits
 * between messages, so it is kept out of the batches (the scroll, unread and
 * mark-seen logic read only real messages) and both apps draw it after the
 * latest batch. */






















const SCHEDULED_ACTIONS = ['edit', 'changeTime', 'sendNow', 'cancel']
const FAILED_ACTIONS = ['retry', 'reschedule', 'dismiss']
const UNRETRYABLE_ACTIONS = ['dismiss']
const NO_ACTIONS = []
const NO_SCHEDULED_ROWS = []

// A teammate or an org admin sees the bubble, labelled with its author, and
// may only cancel a message still waiting; a failed or sending one is
// read-only to them. Other staff in a patient chat ('viewer') only see it.
const CANCEL_ONLY_ACTIONS = ['cancel']

const scheduledFeedItem = (
  row, now, role, timeZone,
) => {
  // A reply ends the label; the quote itself sits inside the bubble.
  const replyTo = row.ReplyToMessageKey ? ', replying to a message' : ''
  const by = `by ${row.AuthorName || 'someone'}${replyTo}`
  const base = {
    MessageType: 'scheduled' ,
    MessageKey: `scheduled:${row.ScheduledMessageKey}`,
    Scheduled: row,
    TimeText: _scheduledMessages.formatScheduledTime.call(void 0, row.SendAt, now, timeZone),
    AuthorName: row.AuthorName,
    Byline: row.AuthorName,
    FailureText: null,
    FailureDetail: null,
  }
  if (row.Status === 'failed') {
    const failure = _scheduledMessages.failureReasonText.call(void 0, row.FailureReason)
    const reason = `${failure.charAt(0).toLowerCase()}${failure.slice(1)}`
    return {
      ...base,
      Heading: failure,
      FailureText: failure,
      FailureDetail: row.FailureDetail || null,
      AccessibilityLabel: `Scheduled message, ${reason}, was due ${_scheduledMessages.spokenScheduledTime.call(void 0, row.SendAt, now, timeZone)}, ${by}`,
      Actions: role !== 'author'
        ? NO_ACTIONS
        : row.FailureReason && exports.UNRETRYABLE_FAILURE_REASONS.has(row.FailureReason) ? UNRETRYABLE_ACTIONS : FAILED_ACTIONS,
    }
  }
  if (row.Status === 'sending') {
    return { ...base, Heading: 'Sending', AccessibilityLabel: `Scheduled message, sending now, ${by}`, Actions: NO_ACTIONS }
  }
  // Someone else's: "Scheduled by Parsa Khazaeepoul · Tomorrow, 9:00 AM".
  const isSomeoneElses = role !== 'author' && !!row.AuthorName
  return {
    ...base,
    Heading: isSomeoneElses ? `Scheduled by ${row.AuthorName}` : 'Scheduled',
    Byline: isSomeoneElses ? '' : row.AuthorName,
    AccessibilityLabel: `Scheduled message, sends ${_scheduledMessages.spokenScheduledTime.call(void 0, row.SendAt, now, timeZone)}, ${by}`,
    Actions: role === 'author' ? SCHEDULED_ACTIONS : role === 'viewer' ? NO_ACTIONS : CANCEL_ONLY_ACTIONS,
  }
}

/* A chat's scheduled rows (by send time, as the slice keeps them) as feed
 * items, latest first like the message batches: index 0 is the bottom. With
 * a viewer, only the rows they may see, with the actions their role allows;
 * the server sends no others, and this keeps stale state from showing one.
 * Without one, every row is treated as the viewer's own. */
 const processScheduledMessages = (
  rows,
  now = new Date(),
  viewer,
  // The chat's type (its WrappedType), which decides what teammates see.
  chatType,
  // The zone the times read in; the device's when not given.
  timeZone,
) => {
  const items = []
  rows.forEach(row => {
    const role = viewer ? exports.scheduledRoleFor.call(void 0, row, viewer, chatType) : 'author'
    if (role) items.push(scheduledFeedItem(row, now, role, timeZone))
  })
  return items.length ? items.reverse() : NO_SCHEDULED_ROWS
}; exports.processScheduledMessages = processScheduledMessages
